import { useCallback, useState } from 'react';
import { showNotif } from '@p81-common/ui';
import type { TFunction } from 'i18next';
import { AssociationOutcome, RuleViolation, TenantValidation } from './types';

/** What the result screen needs to know about the attempt that produced it. */
export interface ValidationResult {
  tenants: TenantValidation[];
  validCount: number;
  invalidCount: number;
}

/**
 * The single predicate for "nothing started". Both the copy and the colour
 * scheme of the result screen hang off this, and deriving it twice let them
 * disagree on a 202 that accepted no tenants.
 */
export const nothingStarted = ({ validCount }: ValidationResult): boolean =>
  validCount === 0;

export interface OutcomeVerdict {
  /** Where the flow goes next. `stay` means the wizard keeps its own screen. */
  screen: 'stay' | 'validation-result' | 'started';
  /** A batch is running: reset the wizard inputs and refresh the tenant. */
  batchStarted: boolean;
}

/**
 * Owns everything the mode-select components do with an association outcome.
 * Both of them answer the same four ways and used to carry a copy of this
 * branching each - the one part of the flow with no test coverage, kept in sync
 * by hand.
 *
 * `failureKey` is the i18n prefix holding RUN_FAILED / RUN_FAILED_FALLBACK for
 * the flow that submitted.
 */
export const useAssociationOutcome = (t: TFunction) => {
  // Batch the last migration started - scopes the Datadog link to that batch.
  const [migrationBatchId, setMigrationBatchId] = useState<string>();
  // Upfront validation failures from the last attempt. Shown inline on the
  // confirm step, so this must never clear the credentials the operator typed.
  const [upfrontErrors, setUpfrontErrors] = useState<RuleViolation[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult>();

  const clearUpfrontErrors = useCallback(() => setUpfrontErrors([]), []);

  const handleOutcome = useCallback(
    (outcome: AssociationOutcome, failureKey: string): OutcomeVerdict => {
      // Fixable from the wizard's own inputs - stay put and show them there.
      if (outcome.kind === 'upfront-rejected') {
        setUpfrontErrors(outcome.errors);
        return { screen: 'stay', batchStarted: false };
      }

      setUpfrontErrors([]);

      if (outcome.kind === 'failed') {
        const message = outcome.message
          ? t(`${failureKey}.RUN_FAILED`, { message: outcome.message })
          : t(`${failureKey}.RUN_FAILED_FALLBACK`);
        showNotif(message, { alertProps: { severity: 'error' } });
        return { screen: 'stay', batchStarted: false };
      }

      setMigrationBatchId(outcome.batchId);

      if (outcome.kind === 'tenants-rejected') {
        setValidationResult({
          tenants: outcome.tenants,
          validCount: 0,
          invalidCount: outcome.invalidCount
        });
        return { screen: 'validation-result', batchStarted: false };
      }

      // Accepted. Skipped tenants have to be named, or a 1-of-N batch reads
      // exactly like an N-of-N one.
      if (outcome.invalidCount > 0) {
        setValidationResult({
          tenants: outcome.tenants,
          validCount: outcome.validCount,
          invalidCount: outcome.invalidCount
        });
        return { screen: 'validation-result', batchStarted: true };
      }

      return { screen: 'started', batchStarted: true };
    },
    [t]
  );

  return {
    migrationBatchId,
    upfrontErrors,
    validationResult,
    clearUpfrontErrors,
    handleOutcome
  };
};
